<template>
    <div class="flex w-full flex-col items-center gap-6 max-w-5xl">
        <div class="text-center leading-tight">
            <div class="max-w-2xl display-medium text-balance mb-2">
                <span class="gradient-text">This is what Day 60 feels like</span>
            </div>
            <p class="text-valence-grey-750 body md:display-serif max-w-2xl mx-auto text-pretty">Nadia meets the user with proactive insights based on the context she has.</p>
        </div>

        <!-- Simulated product screenshot -->
        <div class="w-full rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
            <!-- Top bar -->
            <div class="flex items-center justify-between border-b border-gray-100 px-4 py-2.5 bg-gray-50/50">
                <div class="flex items-center gap-3">
                    <div class="flex gap-1.5">
                        <div class="h-3 w-3 rounded-full bg-red-400"></div>
                        <div class="h-3 w-3 rounded-full bg-yellow-400"></div>
                        <div class="h-3 w-3 rounded-full bg-green-400"></div>
                    </div>
                    <div class="rounded-md bg-gray-100 px-3 py-1 text-xs text-gray-400">coach.valence.co</div>
                </div>
            </div>

            <div class="flex max-h-[460px]">
                <!-- Sidebar -->
                <div class="w-48 shrink-0 border-r border-gray-100 bg-white p-4 flex flex-col gap-1">
                    <div class="flex items-center gap-2 mb-4">
                        <CoachingModeMarble class="w-6 h-6" />
                        <span class="text-sm font-semibold text-gray-900">Nadia</span>
                    </div>
                    <div class="flex items-center gap-2.5 rounded-lg bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700">
                        <i class="bi bi-house-door text-base" aria-hidden="true"></i> Home
                    </div>
                    <div class="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                        <i class="bi bi-chat-dots text-base" aria-hidden="true"></i> Sessions
                    </div>
                    <div class="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                        <i class="bi bi-lightning-charge text-base" aria-hidden="true"></i> My Work
                    </div>
                    <div class="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                        <i class="bi bi-people text-base" aria-hidden="true"></i> Your People
                    </div>
                    <div class="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                        <i class="bi bi-person text-base" aria-hidden="true"></i> Profile
                    </div>
                </div>

                <!-- Main content -->
                <div class="flex-1 p-6 overflow-y-auto">
                    <!-- Greeting -->
                    <div class="mb-6">
                        <h2 class="text-xl font-semibold text-gray-900 mb-1">Good morning, Sarah.</h2>
                        <p class="text-sm text-gray-500">Here's what Nadia is thinking about for you today.</p>
                    </div>

                    <!-- Proactive coaching card -->
                    <div class="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50/80 to-white p-5 mb-5">
                        <div class="flex items-start gap-3">
                            <div class="w-8 h-8 shrink-0 mt-0.5">
                                <CoachingModeMarble />
                            </div>
                            <div class="flex flex-col gap-2 flex-1">
                                <p class="text-sm text-gray-800 leading-relaxed">
                                    {{ samplePrompts[activePromptIndex].message }}
                                </p>
                                <div class="flex flex-wrap gap-1.5">
                                    <span
                                        v-for="tag in samplePrompts[activePromptIndex].contextTags"
                                        :key="tag.label"
                                        class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
                                        :class="tag.classes">
                                        <span class="h-1.5 w-1.5 rounded-full" :class="tag.dotClass"></span>
                                        {{ tag.label }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <!-- Prompt navigation dots -->
                        <div class="flex items-center justify-center gap-1.5 mt-3">
                            <button
                                v-for="(_, idx) in samplePrompts"
                                :key="idx"
                                class="h-1.5 rounded-full transition-all duration-200"
                                :class="activePromptIndex === idx ? 'w-5 bg-indigo-500' : 'w-1.5 bg-indigo-200 hover:bg-indigo-300'"
                                :aria-label="`View example ${idx + 1}`"
                                @click="activePromptIndex = idx" />
                        </div>
                    </div>

                    <!-- Insights row -->
                    <div>
                        <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Weekly insights</h3>
                        <div class="grid grid-cols-3 gap-3">
                            <div v-for="insight in sampleInsights" :key="insight.title" class="rounded-lg border border-gray-200 bg-white p-3.5 flex flex-col gap-1.5">
                                <div class="flex items-center gap-1.5">
                                    <div class="h-1.5 w-1.5 rounded-full" :class="insight.dotColor"></div>
                                    <span class="text-[11px] font-medium text-gray-400">{{ insight.category }}</span>
                                </div>
                                <h4 class="text-xs font-semibold text-gray-900 leading-snug">{{ insight.title }}</h4>
                                <p class="text-[11px] text-gray-500 leading-relaxed line-clamp-2">{{ insight.description }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Your People section -->
                    <div class="mt-5">
                        <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Your People</h3>
                        <div class="flex flex-col gap-2.5">
                            <div v-for="person in peopleDynamics" :key="person.name" class="rounded-lg border border-gray-200 bg-white p-3.5 flex items-center gap-3">
                                <div class="h-8 w-8 rounded-full flex items-center justify-center text-xs font-semibold text-white" :class="person.avatarBg">
                                    {{ person.initials }}
                                </div>
                                <div class="flex-1 min-w-0">
                                    <div class="flex items-center gap-2">
                                        <span class="text-xs font-semibold text-gray-900">{{ person.name }}</span>
                                        <span class="text-[10px] text-gray-400">{{ person.role }}</span>
                                    </div>
                                    <p class="text-[11px] text-gray-600 leading-snug mt-0.5">{{ person.dynamic }}</p>
                                </div>
                                <button class="text-[10px] font-medium text-indigo-600 hover:text-indigo-700 whitespace-nowrap">Chat about this</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex items-center gap-4 mt-2">
            <BaseButton theme="tertiary" @click="emit('back')"> <i class="bi bi-arrow-left mr-1" aria-hidden="true"></i> Back </BaseButton>
            <BaseButton theme="primary" class="w-60 leading-6 body-small" @click="emit('next')"> See how to enable this </BaseButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import CoachingModeMarble from "~vue/components/navigation/CoachingModeMarble.vue";
import { ref } from "vue";

const emit = defineEmits<{
    back: [];
    next: [];
}>();

const activePromptIndex = ref(0);

const samplePrompts = [
    {
        message:
            "I noticed your 1:1 with Jordan is tomorrow — last time you mentioned tension around the Q3 roadmap. Want to prepare? I can help you think through how to acknowledge what happened while refocusing on shared priorities.",
        contextTags: [
            {
                label: "Jordan (direct report)",
                dotClass: "bg-valence-light-blue-400",
                classes: "bg-valence-light-blue-50 text-valence-light-blue-800 ring-1 ring-valence-light-blue-200",
            },
            { label: "Q3 roadmap", dotClass: "bg-valence-violet-400", classes: "bg-valence-violet-50 text-valence-violet-800 ring-1 ring-valence-violet-200" },
            { label: "Calendar", dotClass: "bg-valence-gold-600", classes: "bg-[#fef5e1] text-valence-gold-800 ring-1 ring-[#f9d43f]" },
        ],
    },
    {
        message:
            "You've mentioned wanting to get better at delegating three times in the last month — and I've noticed it comes up specifically around design decisions. Would you like to work through a framework for when to hold tight and when to let go?",
        contextTags: [
            { label: "Growth pattern", dotClass: "bg-valence-pink-400", classes: "bg-valence-pink-50 text-valence-pink-800 ring-1 ring-valence-pink-200" },
            { label: "Delegation theme", dotClass: "bg-valence-violet-400", classes: "bg-valence-violet-50 text-valence-violet-800 ring-1 ring-valence-violet-200" },
        ],
    },
    {
        message:
            "Based on how Alex responded to last month's feedback, this performance conversation might land differently if you lead with their recent wins on the infrastructure project before transitioning to growth areas.",
        contextTags: [
            {
                label: "Alex (direct report)",
                dotClass: "bg-valence-light-blue-400",
                classes: "bg-valence-light-blue-50 text-valence-light-blue-800 ring-1 ring-valence-light-blue-200",
            },
            { label: "Performance review", dotClass: "bg-valence-violet-400", classes: "bg-valence-violet-50 text-valence-violet-800 ring-1 ring-valence-violet-200" },
            { label: "Past conversation", dotClass: "bg-gray-400", classes: "bg-gray-100 text-gray-700 ring-1 ring-gray-200" },
        ],
    },
];

const peopleDynamics = [
    {
        name: "Jordan Chen",
        initials: "JC",
        role: "Direct report",
        dynamic: "Tension around autonomy — consider giving more ownership on Q3 deliverables",
        avatarBg: "bg-valence-light-blue-600",
    },
    {
        name: "Sarah Mitchell",
        initials: "SM",
        role: "Peer",
        dynamic: "Strong collaboration momentum — your last 3 interactions were high-energy and productive",
        avatarBg: "bg-emerald-500",
    },
    {
        name: "Alex Rivera",
        initials: "AR",
        role: "Direct report",
        dynamic: "Performance conversation upcoming — lead with recent infrastructure wins",
        avatarBg: "bg-valence-violet-600",
    },
    {
        name: "Dana Park",
        initials: "DP",
        role: "Skip-level",
        dynamic: "Haven't connected in 3 weeks — they recently took on a stretch project",
        avatarBg: "bg-valence-gold-400",
    },
];

const sampleInsights = [
    {
        category: "Leadership pattern",
        title: "Your delegation style is evolving",
        description: "Over the last 3 weeks, you've shifted from asking 'should I do this?' to 'who should own this?'",
        dotColor: "bg-valence-pink-400",
    },
    {
        category: "Relationship dynamics",
        title: "Jordan may need more autonomy",
        description: "In 4 recent conversations about Jordan, the theme of 'letting go' has surfaced.",
        dotColor: "bg-valence-light-blue-600",
    },
    {
        category: "Strategic alignment",
        title: "Q3 priorities are clearer now",
        description: "You've worked through your roadmap tensions across 3 sessions. Your framing has become notably sharper.",
        dotColor: "bg-valence-violet-600",
    },
    {
        category: "Organizational",
        title: "Values alignment opportunity",
        description: "Your recent coaching themes align well with your org's emphasis on psychological safety.",
        dotColor: "bg-valence-gold-600",
    },
];
</script>
